import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

const AUTH_API = environment.apiBaseUrl;

interface BusinessUnit {
  id: number;
  unitName: string;
  unitCode: string;
  unitType: string;
  managerName: string;
  managerEmail: string;
  activationStatus: string;
  description: string;
  expanded: boolean;
}

interface Branch {
  id: number;
  branchName: string;
  branchCode: string;
  branchType: string;
  branchPhone: string;
  branchEmail: string;
  managerName: string;
  activationStatus: string;
  city: string;
  longitude: string;
  latitude: string;
  radius: string;
  currency: string;
  logoPreview: string | null;
  expanded: boolean;
  buExpanded: boolean;
  businessUnits: BusinessUnit[];
  nextUnitId: number;
}

type LocationTarget = {
  city: string;
  latitude: string;
  longitude: string;
  [key: string]: any;
};

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './register.html',
  styleUrl: './register.css',
})
export class RegisterComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);

  private zone = inject(NgZone);

  @Input() selectedApps: { name: string; image: string }[] = [];
  @Output() goBack = new EventEmitter<void>();

  // Core fields
  companyName = '';
  fullName = '';
  username = '';
  email = '';
  phone = '';
  password = '';
  confirmPassword = '';
  gender = '';
  address = '';
  dateOfBirth = '';

  // Country & currency from API
  countries: { name: string; code: string; currencyCode: string; flagEmoji: string; phoneCode: string }[] = [];
  phoneCode = '';
  selectedCountryCode = '';
  selectedCountryFlag = '';
  currencies: { code: string; name: string; symbol: string }[] = [];
  cities: { id: number; name: string; latitude?: number; longitude?: number; postalCode?: string }[] = [];
  countriesLoading = false;
  citiesLoading = false;
  currenciesLoading = false;

  // Country custom dropdown
  showCountryDrop = false;
  countrySearch = '';
  countryFocusIndex = 0;

  get filteredCountries() {
    const q = this.countrySearch.toLowerCase().trim();
    return q ? this.countries.filter(c => c.name.toLowerCase().includes(q)) : this.countries;
  }

  openCountryDrop(): void {
    this.zone.run(() => {
      this.countrySearch = '';
      this.countryFocusIndex = 0;
      this.showCountryDrop = true;
      this.cdr.detectChanges();
    });
    setTimeout(() => {
      const input = document.querySelector('.country-search-input') as HTMLInputElement;
      if (input) input.focus();
    });
  }

  onCountryTriggerKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.openCountryDrop();
    }
  }

  onCountryBlur(): void {
    setTimeout(() => {
      this.showCountryDrop = false;
      this.cdr.detectChanges();
    }, 150);
  }

  selectCountry(c: any): void {
    this.address = c.name;
    this.showCountryDrop = false;
    this.countrySearch = '';
    this.countryFocusIndex = 0;
    this.onCountryChange(c.name);
    this.cdr.detectChanges();
  }

  onCountryKeydown(event: KeyboardEvent): void {
    const list = this.filteredCountries;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.countryFocusIndex = Math.min(this.countryFocusIndex + 1, list.length - 1);
      this.scrollCountryOption();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.countryFocusIndex = Math.max(this.countryFocusIndex - 1, 0);
      this.scrollCountryOption();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (list[this.countryFocusIndex]) this.selectCountry(list[this.countryFocusIndex]);
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      this.showCountryDrop = false;
    }
  }

  private scrollCountryOption(): void {
    setTimeout(() => {
      const el = document.querySelector(`.country-option[data-idx="${this.countryFocusIndex}"]`) as HTMLElement;
      if (el) el.scrollIntoView({ block: 'nearest' });
    });
  }

  // Password eye toggles
  showPassword = false;
  showConfirmPassword = false;

  // UI state
  isLoading = false;
  errorMessage = '';
  showSuccessPopup = false;
  formSubmitted = false;

  // Seed sample data checkbox
  seedSampleData = false;

  // Setup screen
  showSetupScreen = false;
  setupProgress = 0;
  setupModuleIndex = 0;
  setupTipIndex = 0;
  tipVisible = true;
  private setupIntervalRef: any = null;
  private tipTimer: any = null;


  readonly setupModules = [
    { name: 'Accounting',      icon: 'accounting' },
    { name: 'CRM',             icon: 'crm' },
    { name: 'Human Resources', icon: 'hr' },
    { name: 'Inventory',       icon: 'inventory' },
    { name: 'Manufacturing',   icon: 'manufacturing' },
    { name: 'Procurement',     icon: 'procurement' },
    { name: 'Sales',           icon: 'sales' },
  ];

  readonly setupTips = [
    // Accounting
    'Chart of Accounts structures every financial transaction in your business',
    'Double-entry bookkeeping: every debit has an equal and opposite credit',
    'Aged receivables reports show how long invoices have been outstanding',
    'Bank reconciliation matches your ledger against actual bank statements',
    // CRM
    'Leads are unqualified prospects — Opportunities are leads worth pursuing',
    'Sales pipelines give your team clear visibility into deals at every stage',
    'Activity logging keeps a complete history of every customer interaction',
    'Customer segmentation lets you target the right audience at the right time',
    // Human Resources
    'Payroll automation reduces errors and ensures staff are paid on time',
    'Leave policies can be configured by department, grade, or contract type',
    'Employee self-service lets staff apply for leave and view their payslips',
    'Performance cycles help align individual goals with company objectives',
    // Inventory
    'FIFO — First In, First Out — is the most common stock valuation method',
    'Reorder points trigger purchase orders before your stock runs out',
    'Batch and serial tracking provides end-to-end product traceability',
    'Cycle counting keeps inventory records accurate without full stocktakes',
    // Manufacturing
    'A Bill of Materials (BOM) lists every component needed to build a product',
    'Work orders track each production step from queue through to completion',
    'Capacity planning ensures machines and staff are never over-allocated',
    'Scrap and yield tracking improves production efficiency over time',
    // Procurement
    'Purchase requisitions are internal requests to buy goods or services',
    'Three-way matching verifies PO, goods receipt and invoice before payment',
    'Approved vendor lists keep your supply chain compliant and controlled',
    'Blanket orders let you lock in pricing for recurring supplier purchases',
    // Sales
    'Quotations become sales orders once a customer confirms the price',
    'Invoicing automatically triggers the accounts receivable workflow',
    'Sales dashboards give managers real-time visibility into team performance',
    'Price lists let you offer different rates to different customer groups',
  ];

  readonly progressCircumference = 2 * Math.PI * 50;

  get progressOffset(): number {
    return this.progressCircumference * (1 - this.setupProgress / 100);
  }

  get currentSetupModule(): string {
    return this.setupModules[this.setupModuleIndex]?.name ?? '';
  }

  get currentSetupTip(): string {
    return this.setupTips[this.setupTipIndex];
  }

  isModuleDone(index: number): boolean {
    return index < this.setupModuleIndex;
  }

  isModuleActive(index: number): boolean {
    return index === this.setupModuleIndex;
  }

  startSetupAnimation(): void {
    this.setupProgress = 0;
    this.setupModuleIndex = 0;
    this.setupTipIndex = 0;

    this.setupIntervalRef = setInterval(() => {
      this.zone.run(() => {
        if (this.setupProgress >= 85) {
          clearInterval(this.setupIntervalRef);
          this.setupIntervalRef = null;
          return;
        }
        this.setupProgress = Math.min(85, this.setupProgress + 1);
        this.setupModuleIndex = Math.min(
          Math.floor((this.setupProgress / 100) * this.setupModules.length),
          this.setupModules.length - 1,
        );
        this.cdr.detectChanges();
      });
    }, 80);
  }

  private completeSeedAnimation(): void {
    if (this.setupIntervalRef) {
      clearInterval(this.setupIntervalRef);
      this.setupIntervalRef = null;
    }
    const fillTimer = setInterval(() => {
      this.zone.run(() => {
        this.setupProgress = Math.min(100, this.setupProgress + 3);
        this.setupModuleIndex = Math.min(
          Math.floor((this.setupProgress / 100) * this.setupModules.length),
          this.setupModules.length - 1,
        );
        this.cdr.detectChanges();
        if (this.setupProgress >= 100) {
          clearInterval(fillTimer);
          this.setupModuleIndex = this.setupModules.length - 1;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.zone.run(() => this.showWelcomeAndRedirect());
          }, 600);
        }
      });
    }, 50);
  }

  private showWelcomeAndRedirect(): void {
    this.showSetupScreen = false;
    this.cdr.detectChanges();

    // Inject styles
    const style = document.createElement('style');
    style.textContent = `
      #oy-ws{position:fixed;inset:0;background:#1b2333;z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;padding:24px}
      #oy-ws h1{font:700 44px/1.2 Inter,sans-serif;color:#fff;margin:0;opacity:0;transform:translateY(24px);transition:opacity .9s ease,transform .9s ease}
      #oy-ws p{font:400 17px/1.6 Inter,sans-serif;color:rgba(255,255,255,.65);margin:0;max-width:520px;text-align:center;opacity:0;transform:translateY(14px);transition:opacity .9s ease,transform .9s ease}
      #oy-ws small{font:500 14px/1 Inter,sans-serif;color:rgba(255,255,255,.4);margin-top:8px;opacity:0;transition:opacity .7s ease;display:flex;align-items:center;gap:8px}
      #oy-ws .oy-dot{width:8px;height:8px;border-radius:50%;background:#009ebf;animation:oypulse 1.2s ease-in-out infinite}
      @keyframes oypulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.6)}}
      .oy-v{opacity:1!important;transform:translateY(0)!important}
    `;
    document.head.appendChild(style);

    // Build and inject welcome screen into body
    const ws = document.createElement('div');
    ws.id = 'oy-ws';
    ws.innerHTML = `
      <h1 id="oy-wh">Welcome to <span style="color:#009ebf">OrderYaa</span></h1>
      <p  id="oy-wt">the only platform you will ever need to transform all your activities.</p>
      <small id="oy-wb"><span class="oy-dot"></span>Building your workspace...</small>
    `;
    document.body.appendChild(ws);

    setTimeout(() => document.getElementById('oy-wh')?.classList.add('oy-v'),  100);
    setTimeout(() => document.getElementById('oy-wt')?.classList.add('oy-v'), 1400);
    setTimeout(() => document.getElementById('oy-wb')?.classList.add('oy-v'), 3000);
    setTimeout(() => this.zone.run(() => this.loginAndRedirect()),             5200);
  }

  private stopSeedAnimation(): void {
    if (this.setupIntervalRef) {
      clearInterval(this.setupIntervalRef);
      this.setupIntervalRef = null;
    }
    if (this.tipTimer) {
      clearInterval(this.tipTimer);
      this.tipTimer = null;
    }
    this.showSetupScreen = false;
    this.setupProgress = 0;
    this.setupModuleIndex = 0;
  }

  private loginAndRedirect(attempt = 0): void {
    // attempt 0: full email, attempt 1: email prefix fallback
    const loginUsername = attempt === 0 ? this.email : this.email.split('@')[0];

    this.http
      .post<any>(`${AUTH_API}/api/Auth/login`, {
        username: loginUsername,
        password: this.password,
      })
      .subscribe({
        next: (res) => {
          const token =
            res?.data?.accessToken ||
            res?.data?.token ||
            res?.accessToken ||
            res?.token ||
            '';
          const base = environment.erpBaseUrl;
          if (token) {
            this.doSelectCompany(res?.data?.user?.companyId, token);
          } else {
            window.location.href = base;
          }
        },
        error: () => {
          if (attempt === 0) {
            setTimeout(() => {
              this.zone.run(() => this.loginAndRedirect(1));
            }, 1000);
          } else {
            window.location.href = environment.erpBaseUrl;
          }
        },
      });
  }

  private doSelectCompany(companyId: string | undefined, preAuthToken: string): void {
    const base = environment.erpBaseUrl;
    if (!companyId) {
      window.location.href = `${base}?token=${preAuthToken}`;
      return;
    }
    this.http
      .post<any>(`${AUTH_API}/api/Auth/select-company`, {
        preAuthToken,
        companyId,
      })
      .subscribe({
        next: (res) => {
          const token = res?.data?.accessToken || res?.data?.token || preAuthToken;
          window.location.href = `${base}?token=${token}`;
        },
        error: () => {
          window.location.href = `${base}?token=${preAuthToken}`;
        },
      });
  }

  ngOnInit(): void {
    this.countriesLoading = true;
    this.currenciesLoading = true;

    this.http.get<any>(`${AUTH_API}/api/core/geo/countries`).subscribe({
      next: (res) => {
        this.zone.run(() => {
          this.countries = (res?.data ?? [])
            .filter((c: any) => c.isActive !== false)
            .map((c: any) => ({ ...c, flagEmoji: this.codeToFlagEmoji(c.code) }));
          this.countriesLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.zone.run(() => {
          this.countriesLoading = false;
          this.cdr.detectChanges();
        });
      }
    });

    this.http.get<any>(`${AUTH_API}/api/core/geo/currencies`).subscribe({
      next: (res) => {
        this.zone.run(() => {
          this.currencies = res?.data ?? [];
          this.currenciesLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.zone.run(() => {
          this.currenciesLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.setupIntervalRef) {
      clearInterval(this.setupIntervalRef);
    }
    if (this.tipTimer) {
      clearInterval(this.tipTimer);
    }
  }

  onCountryChange(countryName: string): void {
    const found = this.countries.find(c => c.name === countryName);
    if (found) {
      if (found.currencyCode) {
        this.currency = found.currencyCode;
      }
      if (found.phoneCode) {
        this.phoneCode = found.phoneCode.startsWith('+') ? found.phoneCode : '+' + found.phoneCode;
      }
      this.selectedCountryCode = (found.code || 'PK').toLowerCase();
      this.selectedCountryFlag = found.flagEmoji || '';

      this.city = '';
      this.cities = [];
      this.citiesLoading = true;
      this.cdr.detectChanges();
      const code = (found.code || 'PK').toUpperCase();
      this.http.get<any>(`${AUTH_API}/api/core/geo/countries/${code}/cities?limit=500`)
        .pipe(finalize(() => {
          this.zone.run(() => {
            this.citiesLoading = false;
            this.cdr.detectChanges();
          });
        }))
        .subscribe({
          next: (res) => {
            this.zone.run(() => {
              const raw: any[] = Array.isArray(res?.data) ? res.data
                : Array.isArray(res?.result) ? res.result
                : Array.isArray(res) ? res : [];
              this.cities = raw.slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
              this.cdr.detectChanges();
            });
          },
          error: () => {
            this.zone.run(() => {
              this.cities = [];
              this.cdr.detectChanges();
            });
          }
        });
    }
    this.syncBranch();
  }

  onCityChange(cityName: string): void {
    const found = this.cities.find(c => c.name === cityName);
    if (found) {
      if (found.latitude)   this.latitude  = String(found.latitude);
      if (found.longitude)  this.longitude = String(found.longitude);
      if (found.postalCode) this.postalCode = found.postalCode;
    }
    this.syncBranch();
  }

  private codeToFlagEmoji(code: string): string {
    if (!code || code.length !== 2) return '🌐';
    const offset = 0x1F1E6 - 65;
    return [...code.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + offset)).join('');
  }

  get subdomain(): string {
    return (
      this.companyName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') ||
      'your-company'
    );
  }

  // Advance fields
  advanceExpanded = false;
  city = '';
  state = '';
  postalCode = '';
  longitude = '';
  latitude = '';
  radius = '';
  currency = '';
  companyLogoPreview: string | null = null;

  // Branches
  branchesExpanded = false;
  branches: Branch[] = [
    {
      id: 1,
      branchName: 'Main Branch',
      branchCode: '',
      branchType: '',
      branchPhone: '',
      branchEmail: '',
      managerName: '',
      activationStatus: 'Active',
      city: '',
      longitude: '',
      latitude: '',
      radius: '',
      currency: '',
      logoPreview: null,
      expanded: true,
      buExpanded: false,
      businessUnits: [],
      nextUnitId: 1,
    },
  ];
  private nextBranchId = 2;

  // GUID helper
  private newGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // Sync top-level + advance into first branch
  syncBranch(): void {
    if (!this.branches.length) return;
    const b = this.branches[0];

    b.branchName = this.companyName || 'Main Branch';
    b.managerName = this.fullName;
    b.branchEmail = this.email;
    b.branchPhone = this.phone;

    b.city = this.city;
    b.longitude = this.longitude;
    b.latitude = this.latitude;
    b.radius = this.radius;
    b.currency = this.currency;
    b.logoPreview = this.companyLogoPreview;
  }

  // ── Map picker ──
  showMapModal = false;
  mapSearchQuery = '';
  private mapTarget: 'company' | LocationTarget | null = null;
  private leafletMap: any = null;
  private leafletMarker: any = null;
  private pickedLat = 0;
  private pickedLng = 0;
  private pickedCity = '';

  openMapPicker(target: 'company' | LocationTarget): void {
    this.mapTarget = target;
    this.mapSearchQuery = '';
    this.showMapModal = true;
    setTimeout(() => this.initLeafletMap(), 200);
  }

  private initLeafletMap(): void {
    const L = (window as any)['L'];
    if (!L) return;

    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }

    this.leafletMap = L.map('map-picker').setView([30.3753, 69.3451], 6);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
    }).addTo(this.leafletMap);

    this.leafletMap.on('click', (e: any) => this.placeMarker(e.latlng.lat, e.latlng.lng));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        this.leafletMap.setView([pos.coords.latitude, pos.coords.longitude], 13);
        this.placeMarker(pos.coords.latitude, pos.coords.longitude);
      });
    }
  }

  private placeMarker(lat: number, lng: number): void {
    const L = (window as any)['L'];
    if (!L) return;

    if (this.leafletMarker) this.leafletMarker.remove();
    this.leafletMarker = L.marker([lat, lng]).addTo(this.leafletMap);

    this.pickedLat = lat;
    this.pickedLng = lng;

    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then((r) => r.json())
      .then((data) => {
        this.pickedCity =
          data.address?.city ||
          data.address?.town ||
          data.address?.village ||
          data.address?.county ||
          '';
      })
      .catch(() => {});
  }

  searchMapLocation(): void {
    if (!this.mapSearchQuery.trim()) return;

    fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        this.mapSearchQuery,
      )}&format=json&limit=1`,
    )
      .then((r) => r.json())
      .then((results) => {
        if (results.length > 0) {
          const lat = parseFloat(results[0].lat);
          const lng = parseFloat(results[0].lon);
          this.leafletMap.setView([lat, lng], 13);
          this.placeMarker(lat, lng);
        }
      })
      .catch(() => {});
  }

  confirmLocation(): void {
    if (!this.mapTarget || !this.pickedLat) return;

    const lat = this.pickedLat.toFixed(6);
    const lng = this.pickedLng.toFixed(6);

    if (this.mapTarget === 'company') {
      this.latitude = lat;
      this.longitude = lng;
      this.city = this.pickedCity;
    } else {
      this.mapTarget.latitude = lat;
      this.mapTarget.longitude = lng;
      this.mapTarget.city = this.pickedCity;
    }

    this.syncBranch();
    this.closeMap();
  }

  closeMap(): void {
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
    this.leafletMarker = null;
    this.showMapModal = false;
    this.mapTarget = null;
  }

  // ── Branch & BU helpers ──
  toggleBranch(id: number): void {
    this.branches.forEach((b) => (b.expanded = b.id === id ? !b.expanded : false));
  }

  toggleBranchBU(id: number): void {
    this.branches.forEach((b) => {
      if (b.id === id) {
        b.buExpanded = !b.buExpanded;
        if (b.buExpanded) b.expanded = false;
      } else {
        b.buExpanded = false;
      }
    });
  }

  addBranch(): void {
    this.branches.forEach((b) => (b.expanded = false));
    this.branches.push({
      id: this.nextBranchId++,
      branchName: '',
      branchCode: '',
      branchType: '',
      branchPhone: this.phone,
      branchEmail: this.email,
      managerName: this.fullName,
      activationStatus: 'Active',
      city: '',
      longitude: '',
      latitude: '',
      radius: '',
      currency: '',
      logoPreview: null,
      expanded: true,
      buExpanded: false,
      businessUnits: [],
      nextUnitId: 1,
    });
  }

  removeBranch(id: number): void {
    this.branches = this.branches.filter((b) => b.id !== id);
  }

  toggleBusinessUnit(branch: Branch, id: number): void {
    branch.businessUnits.forEach((u) => (u.expanded = u.id === id ? !u.expanded : false));
  }

  addBusinessUnit(branch: Branch): void {
    branch.businessUnits.forEach((u) => (u.expanded = false));
    branch.businessUnits.push({
      id: branch.nextUnitId++,
      unitName: '',
      unitCode: '',
      unitType: '',
      managerName: this.fullName,
      managerEmail: this.email,
      activationStatus: 'Active',
      description: '',
      expanded: true,
    });
  }

  removeBusinessUnit(branch: Branch, id: number): void {
    branch.businessUnits = branch.businessUnits.filter((u) => u.id !== id);
  }

  // ✅ Upload fix: 1st time hi preview show + same file reselect issue fix
  onLogoUpload(event: Event, target: 'company' | LocationTarget): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      this.zone.run(() => {
        const result = reader.result as string;
        if (target === 'company') this.companyLogoPreview = result;
        else target['logoPreview'] = result;

        this.syncBranch();
        this.cdr.detectChanges();
      });
    };

    reader.readAsDataURL(file);

    // important: allow selecting same file again later
    input.value = '';
  }

  // ── Submit with API ──
  onSubmit(): void {
    if (this.isLoading) return;

    this.errorMessage = '';
    this.showSuccessPopup = false;

    this.formSubmitted = true;

    if (
      !this.companyName ||
      !this.fullName ||
      !this.email ||
      !this.phone ||
      !this.password ||
      !this.confirmPassword ||
      !this.currency
    ) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    if (!this.address) {
      this.errorMessage = 'Please select a country.';
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.errorMessage = 'Passwords do not match.';
      return;
    }

    // Phone normalize — use selected country's dial code
    let phoneNumber = this.phone.trim();
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = this.phoneCode + phoneNumber.replace(/^0+/, '');
    }

    // Auto-generate codes from company name
    const cleanName = this.companyName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6) || 'COMP';
    const ts = Date.now().toString().slice(-4);
    const companyCode = cleanName + ts;
    const branchCode  = cleanName + 'BR' + ts;
    const buCode      = cleanName + 'BU' + ts;
    const websiteUrl  = 'https://' + this.subdomain + '.' + environment.erpDomain;
    const stateVal    = this.state.trim() || this.city || this.address || '-';
    const postalVal   = this.postalCode.trim() || '00000';

    // Extract base64 from logo data-URL (strip prefix); fallback to 1x1 transparent PNG
    const defaultLogo = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    let logoBase64 = defaultLogo;
    if (this.companyLogoPreview) {
      const comma = this.companyLogoPreview.indexOf(',');
      logoBase64 = comma >= 0 ? this.companyLogoPreview.slice(comma + 1) : this.companyLogoPreview;
    }

    const lat    = this.latitude  ? parseFloat(this.latitude)  : 0;
    const lng    = this.longitude ? parseFloat(this.longitude) : 0;
    const radius = this.radius    ? parseInt(this.radius, 10)  : 0;
    const city   = this.city || this.address;

    const payload = {
      company: {
        code: companyCode,
        companyName: this.companyName,
        legalName: this.companyName,
        registrationNumber: 'REG-' + companyCode,
        baseCurrencyCode: this.currency,
        phoneNumber,
        mobileNumber: phoneNumber,
        contactPerson: this.fullName,
        email: this.email,
        websiteUrl,
        companyLogo: logoBase64,
        latitude: lat,
        longitude: lng,
        radiusInMeters: radius,
        country: this.address,
        countryCode: this.selectedCountryCode.toUpperCase(),
        streetAddress: this.city || this.address,
        city,
        state: stateVal,
        postalCode: postalVal,
        branches: [{
          code: branchCode,
          name: this.companyName + ' Branch',
          branchType: 'Main',
          phoneNumber,
          email: this.email,
          managerName: this.fullName,
          branchLogo: logoBase64,
          country: this.address,
          countryCode: this.selectedCountryCode.toUpperCase(),
          streetAddress: this.city || this.address,
          city,
          state: stateVal,
          postalCode: postalVal,
          latitude: lat,
          longitude: lng,
          isActive: true,
          businessUnits: [{
            code: buCode,
            name: this.companyName + ' Business Unit',
            unitType: 'Default',
            description: 'Default business unit created with the company registration.',
            managerName: this.fullName,
            managerEmail: this.email,
            isActive: true
          }]
        }]
      },
      user: {
        email: this.email,
        password: this.password,
        fullName: this.fullName,
        userName: this.email,
        gender: this.gender || 'Other',
        address: this.address,
        dateOfBirth: this.dateOfBirth || null,
        phoneNumber
      },
      includeSampleData: this.seedSampleData
    };

    this.isLoading = true;
    this.showSetupScreen = true;
    this.cdr.detectChanges();
    this.startSetupAnimation();

    this.http
      .post<any>(`${AUTH_API}/api/Company`, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          if (res?.success) {
            this.completeSeedAnimation();
          } else {
            this.stopSeedAnimation();
            this.errorMessage = res?.message || 'Registration failed. Please try again.';
          }
        },
        error: (err: HttpErrorResponse) => {
          this.zone.run(() => {
            this.stopSeedAnimation();
            const errBody = err.error;
            const isValidationError = errBody?.errors && typeof errBody.errors === 'object' && Object.keys(errBody.errors).length > 0;
            if (isValidationError) {
              const first = Object.values(errBody.errors)[0];
              this.errorMessage = Array.isArray(first) ? (first as string[])[0] : (first as string);
            } else {
              this.errorMessage = errBody?.message || 'Registration failed. Please try again.';
            }
            this.cdr.detectChanges();
          });
        },
      });
  }

  onOk(): void {
    window.location.href = environment.erpBaseUrl;
  }
}